
import BaseBookingLayout from "@/components/base/BaseBookingLayout.vue";
import BookingBasket from "@/components/booking/basket/BookingBasket.vue";
import BookingNav from "@/components/bookingflow/BookingNav.vue";
import BookingStepper from "@/components/bookingflow/BookingStepper.vue";
import BookingCheckout from "@/components/bookingflow/steps/checkout/BookingCheckout.vue";
import BookingDeparture from "@/components/bookingflow/steps/departure/BookingDepartureStep.vue";
import BookingExtras from "@/components/bookingflow/steps/extras/BookingExtras.vue";
import PassengerDetails from "@/components/bookingflow/steps/passengerdetails/PassengerDetailsStep.vue";
import PaymentDetails from "@/components/bookingflow/steps/payment/PaymentDetails.vue";
import TripDetails from "@/components/bookingflow/steps/tripdetails/TripDetails.vue";
import useAuth from "@/composables/useAuth";
import useBooking from "@/composables/useBooking";
import { getQueryParamValue, setQueryParam } from "@/helpers/urlHelper";
import { UserType } from "@/models/enums";
import { snackbarMessageKey } from "@/models/symbols/Snackbar";
import { BookingState } from "@/models/www/store/BookingState";
import { storageService } from "@/services/storage.service";
import { useBookingStore } from "@/store/booking";
import { useUserStore } from "@/store/user";
import { useEventBus } from "@vueuse/core";
import { computed, defineComponent, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import BaseSection from "~/components/base/BaseSection.vue";
import BaseSpinner from "~/components/base/BaseSpinner.vue";
import BookingHeader from "~/components/bookingflow/BookingHeader.vue";

export default defineComponent({
	components: {
		BookingHeader,
		BaseSpinner,
		BaseSection,
		BaseBookingLayout,
		BookingBasket,
		BookingStepper,
		BookingNav,
		TripDetails,
		BookingDeparture,
		BookingExtras,
		PassengerDetails,
		BookingCheckout,
		PaymentDetails,
	},
	setup() {
		const route = useRoute();
		const loading = ref(true);
		const { authenticate } = useAuth();
		const { cancelActiveBooking } = useBooking();
		const userStore = useUserStore();
		const isLoggedIn = computed(() => userStore.getIsLoggedIn);
		const stepComponent = ref();
		const bus = useEventBus(snackbarMessageKey);
		const bookingStore = useBookingStore();

		const activeComponent = computed(() => {
			switch (route.query.step) {
				case "1":
					return "TripDetails";
				case "2":
					return "BookingDeparture";
				case "3":
					return "BookingExtras";
				case "4":
					return "PassengerDetails";
				case "5":
					return "BookingCheckout";
				case "6":
					return "PaymentDetails";
				default:
					return "TripDetails";
			}
		});

		const isPayment = computed(() => {
			return (
				activeComponent.value === "PaymentDetails" ||
				(activeComponent.value === "BookingCheckout" &&
					userStore.getUserType === UserType.Agent)
			);
		});

		const loadingStep = computed(() => {
			return stepComponent.value?.loading;
		});

		//since we want to set a smaller layout at the login pages
		const mainColumnWidth = computed(() => {
			if (route.query.step === "4" && !isLoggedIn.value) {
				return 5;
			} else if (isPayment.value) {
				return 5;
			}

			return 9;
		});

		onMounted(() => {
			//check if we have a route query, if we don't, close the booking
			const step = getQueryParamValue("step");
			if (!step && storageService.getToken()) {
				cancelActiveBooking().then((resp: string) => {
					bus.emit({
						type: "info",
						message: resp,
						id: Date.now().toString(),
					});

					authenticate().then(() => {
						setQueryParam("step", "1");
						loading.value = false;
					});
				});
			} else {
				authenticate(getQueryParamValue("id")).then(
					(state: BookingState | undefined) => {
						if (state) {
							bookingStore.setBooking(state);
						}

						loading.value = false;
					}
				);
			}
		});

		return {
			activeComponent,
			loading,
			mainColumnWidth,
			isPayment,
			stepComponent,
			loadingStep,
		};
	},
});
